import React, { useEffect, useState } from "react";
import { Search, Loader2, RefreshCw, Database, Users } from "lucide-react";
import api from "../../services/api";
import { toast } from "react-toastify";
import BreachDetailModal from "../common/BreachDetailModal";

const formatNum = (n) => (n || 0).toLocaleString();

const stripHtml = (s) => (s || "").replace(/<[^>]*>/g, "");

const BreachDatabase = () => {
  const [breaches, setBreaches] = useState([]);
  const [refreshing, setRefreshing] = useState(false);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const limit = 24;

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      const r = await api.refreshBreachCatalog();
      toast.success(
        `Catalog refreshed: ${r.created} new, ${r.updated} updated`,
      );
      load();
    } catch {
      toast.error("Refresh failed");
    } finally {
      setRefreshing(false);
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.getGlobalBreaches({
        search,
        limit,
        offset: page * limit,
      });
      setBreaches(res.breaches);
      setTotal(res.total);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [search, page]);

  return (
    <div
      className="min-h-screen bg-[#0C0E12] text-gray-300 p-8"
      style={{
        backgroundImage:
          "radial-gradient(circle at 10% 0%, rgba(139,92,246,0.06), transparent 40%), radial-gradient(circle at 90% 100%, rgba(6,182,212,0.05), transparent 40%)",
      }}
    >
      <div className="max-w-6xl mx-auto">
        <div className="flex justify-between items-start mb-2">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Database size={14} className="text-violet-400" />
              <span className="text-[10px] tracking-[0.3em] uppercase text-violet-400 font-mono">
                Global Catalog
              </span>
            </div>
            <h1 className="text-3xl font-bold text-white">
              Breach <span className="text-violet-400">Database</span>
            </h1>
            <p className="text-gray-500 text-sm mt-1">
              Browse the global catalog of known data breach incidents.
            </p>
          </div>
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="flex items-center gap-2 px-4 py-2 bg-violet-500/10 hover:bg-violet-500/20 border border-violet-500/30 hover:border-violet-400/60 text-violet-300 rounded-md font-mono text-xs tracking-wider uppercase disabled:opacity-50 transition-all hover:shadow-[0_0_20px_rgba(139,92,246,0.3)]"
          >
            {refreshing ? (
              <Loader2 className="animate-spin" size={14} />
            ) : (
              <RefreshCw size={14} />
            )}
            Sync Catalog
          </button>
        </div>

        <div className="relative my-6">
          <Search
            className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-600"
            size={16}
          />
          <input
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(0);
            }}
            placeholder="Search by name or domain…"
            className="w-full pl-10 pr-4 py-2.5 bg-[#12151C] border border-white/10 focus:border-violet-500/50 rounded-lg text-white font-mono text-sm focus:outline-none focus:shadow-[0_0_15px_rgba(139,92,246,0.15)] transition-all placeholder-gray-600"
          />
        </div>

        {loading ? (
          <div className="flex justify-center py-20">
            <Loader2 className="animate-spin text-violet-400" size={40} />
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {breaches.map((b) => (
              <button
                key={b.id}
                onClick={() => setSelected(b)}
                className="group relative bg-[#12151C] rounded-xl p-5 border border-white/5 text-left hover:border-violet-500/30 transition-all cursor-pointer hover:shadow-[0_0_25px_rgba(139,92,246,0.15)] overflow-hidden"
              >
                <div className="absolute inset-0 bg-gradient-to-br from-violet-500/0 to-cyan-500/0 group-hover:from-violet-500/5 group-hover:to-cyan-500/5 transition-all pointer-events-none" />

                <div className="relative flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#1a1f2a] to-[#0f1218] border border-white/5 flex items-center justify-center overflow-hidden flex-shrink-0">
                    {b.logoPath ? (
                      <img
                        src={b.logoPath}
                        alt={b.title}
                        className="w-8 h-8 object-contain"
                        onError={(e) => (e.target.style.display = "none")}
                      />
                    ) : (
                      <span className="text-xl font-bold text-violet-400/60">
                        {b.title?.[0] || "?"}
                      </span>
                    )}
                  </div>
                  <div className="min-w-0">
                    <div className="font-semibold text-white truncate group-hover:text-violet-300 transition-colors">
                      {b.title}
                    </div>
                    <div className="text-[10px] text-gray-500 font-mono tracking-wider">
                      {b.breachDate
                        ? new Date(b.breachDate)
                            .toLocaleDateString()
                            .toUpperCase()
                        : "—"}
                    </div>
                  </div>
                </div>

                <p className="relative text-xs text-gray-500 line-clamp-3 mb-3 min-h-[3rem]">
                  {stripHtml(b.description) ||
                    `In ${
                      b.breachDate ? new Date(b.breachDate).getFullYear() : ""
                    }, ${b.title} experienced a data breach.`}
                </p>

                <div className="relative flex flex-wrap gap-1 mb-2">
                  {(b.dataClasses || []).slice(0, 3).map((d) => (
                    <span
                      key={d}
                      className="text-[10px] px-2 py-0.5 bg-cyan-500/5 text-cyan-300 border border-cyan-500/20 rounded font-mono"
                    >
                      {d}
                    </span>
                  ))}
                  {(b.dataClasses || []).length > 3 && (
                    <span className="text-[10px] px-2 py-0.5 bg-white/5 text-gray-400 border border-white/10 rounded font-mono">
                      +{b.dataClasses.length - 3}
                    </span>
                  )}
                </div>

                <div className="relative flex items-center justify-between text-xs pt-3 border-t border-white/5">
                  <span className="flex items-center gap-1.5 text-gray-500 font-mono">
                    <Users size={11} />
                    {formatNum(b.pwnCount)}
                  </span>
                  <span className="text-violet-400 font-mono text-[10px] uppercase tracking-wider">
                    {(b.dataClasses || []).length} types
                  </span>
                </div>
              </button>
            ))}
          </div>
        )}

        <div className="flex justify-between items-center mt-6 bg-[#12151C] border border-white/5 rounded-lg px-4 py-3">
          <button
            disabled={page === 0}
            onClick={() => setPage((p) => p - 1)}
            className="px-4 py-1.5 border border-white/10 hover:border-violet-500/40 text-gray-400 hover:text-violet-300 rounded-md font-mono text-xs uppercase tracking-wider disabled:opacity-30 disabled:cursor-not-allowed transition-all"
          >
            ← Prev
          </button>
          <span className="text-xs text-gray-500 font-mono tracking-wider">
            {total === 0 ? 0 : page * limit + 1}–
            {Math.min((page + 1) * limit, total)} / {total}
          </span>
          <button
            disabled={(page + 1) * limit >= total}
            onClick={() => setPage((p) => p + 1)}
            className="px-4 py-1.5 border border-white/10 hover:border-violet-500/40 text-gray-400 hover:text-violet-300 rounded-md font-mono text-xs uppercase tracking-wider disabled:opacity-30 disabled:cursor-not-allowed transition-all"
          >
            Next →
          </button>
        </div>
      </div>

      {selected && (
        <BreachDetailModal
          breach={selected}
          onClose={() => setSelected(null)}
        />
      )}
    </div>
  );
};

export default BreachDatabase;
